import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  apiErrText, listStaff, signWorkInstruction, signWorkInstructionWithScan,
  setInstructionSeats, setSubcontractInstructionSeats, attachInstructionWetSignature,
  INSTRUCTION_SEAT_LABEL, WI_PART_SIGNED_NOTE,
  type ApiError, type InstructionDetail, type InstructionSeatKey, type MeResponse, type ResolvedSeats, type SeatPatch, type StaffRow,
} from '../lib/api';
import { toast } from './ui/Toast';

/**
 * 9.1.1 三方签字 — the three seats an 工程指令 needs before it is an order at all.
 *
 * Eric 9/18 (工程指令): the picker is 「本项目默认签字人」, NOT a per-instruction assignment. Both routes
 * (`PUT /work-instructions/:wiid/seats` and `PUT /subcontracts/:scId/instruction-seats`) write the SAME
 * `work_instruction_seats` row for the subcontract, and the per-instruction spelling is refused once
 * THAT instruction is issued — because its three signature rows already snapshotted the holders, and
 * re-pointing them would rewrite who signed a live order. So: editing here changes the defaults every
 * FUTURE order on this contract snapshots; a signature already taken is never moved.
 *
 * Who may press 签字:
 *   · the seat holder themselves (`me.account.subject_ref === seat_staff_id`), or
 *   · the 总经理, who must then type a 代签说明 — the server refuses a note-less on-behalf signature.
 * Anyone else gets no button at all rather than a button that 403s (the Layer 1 rule).
 */

const SEATS: InstructionSeatKey[] = ['pm', 'engineer', 'cost'];
const SEAT_FIELD: Record<InstructionSeatKey, keyof SeatPatch> = {
  pm: 'pm_staff_id', engineer: 'engineer_staff_id', cost: 'cost_staff_id',
};

export default function InstructionSignatureSeats({ detail, me, onChanged }: {
  detail: InstructionDetail;
  me: MeResponse | undefined;
  onChanged: () => void;
}) {
  const wi = detail.instruction;
  const [busy, setBusy] = useState(false);
  const [openSeat, setOpenSeat] = useState<InstructionSeatKey | null>(null);
  const role = me?.account?.role ?? '';
  const myStaffId = me?.account?.subject_ref ?? '';
  const isGm = role === 'gm';
  const seats: ResolvedSeats | undefined = detail.seats;
  const isDraft = wi.state === 'draft';
  // The seat EDITOR is open to gm / 预算 / this contract's assigned PM — the same rule the server
  // re-derives in assertMaySetSeats(). A denial here is cosmetic; the server is the gate.
  const canEditSeats = isGm || role === 'budget'
    || (!!myStaffId && seats?.pm.staff_id === myStaffId);   // review 9/18: the agent bearer is refused server-side too

  // The picker only needs to open when somebody actually edits, so the staff list is lazy.
  const [picking, setPicking] = useState(false);
  const { data: staffRows } = useQuery<StaffRow[]>({
    queryKey: ['staff', 'instruction-seats'],
    queryFn: () => listStaff(undefined, { excludePlaceholders: true }),
    enabled: picking,
  });

  async function run(fn: () => Promise<unknown>, okMsg: string) {
    setBusy(true);
    try { await fn(); toast(okMsg, { kind: 'success' }); onChanged(); }
    catch (e) { toast(apiErrText(e), { kind: 'error' }); }
    finally { setBusy(false); }
  }

  async function assign(seat: InstructionSeatKey, staffId: string) {
    const patch: SeatPatch = { [SEAT_FIELD[seat]]: staffId || null };
    // Same row either way; the per-instruction spelling simply refuses after issue, so use the
    // subcontract route once this order has left `draft`.
    await run(
      () => (isDraft ? setInstructionSeats(wi.id, patch) : setSubcontractInstructionSeats(wi.subcontract_id, patch)),
      isDraft ? '本项目默认签字人已更新 / default signers updated' : '本项目默认签字人已更新（本条已下发，改动对下一条生效）/ defaults updated — this issued order keeps its snapshot',
    );
  }

  const signed = detail.signatures.filter((s) => s.signed_at).length;
  const required = detail.signatures_required || 3;
  const partSigned = wi.state === 'pending_signatures' && signed > 0 && signed < required;

  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <strong>✍️ 签字席位 / Signature seats</strong>
        <span className="small muted">合同 9.1.1 — 三方签字齐全后指令方才生效 / three signatures make it an order</span>
        <span className="small muted">· 下方为「本项目默认签字人」，已下发的指令保留下发时的快照 / the picker sets this project's default signers; an issued order keeps its snapshot</span>
        <span style={{ flex: 1 }} />
        <span className={`pill small ${signed >= required ? 'pill-good' : 'pill-warn'}`}>已签 {signed}/{required}</span>
      </div>

      {/* 9.1.1, verbatim. It is the sentence a 劳务 will quote back at settlement, so it is not paraphrased. */}
      {partSigned && (
        <div className="small" style={{ color: 'var(--bad)', marginBottom: 8 }}>
          ⚠ {WI_PART_SIGNED_NOTE} / a part-signed order is not a settlement document.
        </div>
      )}

      <div className="row-wrap" style={{ gap: 10, alignItems: 'stretch' }}>
        {SEATS.map((seat) => {
          const sig = detail.signatures.find((s) => s.seat === seat);
          const holderId = sig?.seat_staff_id ?? seats?.[seat]?.staff_id ?? null;
          const holderName = sig?.seat_staff_name ?? seats?.[seat]?.name ?? null;
          const mine = !!myStaffId && holderId === myStaffId;
          const canSign = wi.state === 'pending_signatures' && !sig?.signed_at && (mine || isGm);
          return (
            <div key={seat} className="card" style={{ flex: '1 1 230px', minWidth: 210, margin: 0, background: 'var(--surface-2, #fff)' }}>
              <div className="small muted">{INSTRUCTION_SEAT_LABEL[seat]}</div>
              <div style={{ fontWeight: 600, margin: '2px 0 6px' }}>
                {holderName ?? <span style={{ color: 'var(--bad)' }}>未指派 / unassigned</span>}
                {seats?.[seat]?.source === 'default' && !sig && <span className="muted small"> （默认 / default）</span>}
              </div>

              {sig?.signed_at ? (
                <div className="small">
                  <span className="pill pill-good small">已签字 / signed</span>
                  <div style={{ marginTop: 4 }}>
                    {sig.signed_by_name ?? '—'} · {new Date(sig.signed_at).toLocaleString('zh-CN')}
                  </div>
                  {sig.on_behalf_of && <div className="muted">代签 / on behalf of {sig.on_behalf_of}</div>}
                  {sig.note_md && <div className="muted">{sig.note_md}</div>}
                </div>
              ) : wi.state === 'draft' ? (
                <span className="pill pill-muted small">未下发 / not issued</span>
              ) : (
                <span className="pill pill-warn small">待签字 / awaiting signature</span>
              )}

              {/* assignment — per-instruction while draft, otherwise the subcontract default */}
              {canEditSeats && (
                <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
                  <label>本项目默认签字人 / this project&apos;s default signer{isDraft ? '' : '（下一条生效 / from the next order）'}</label>
                  <select
                    disabled={busy}
                    value={holderId ?? ''}
                    onFocus={() => setPicking(true)}
                    onChange={(e) => assign(seat, e.target.value)}
                    style={{ minHeight: 44, width: '100%' }}
                  >
                    <option value="">请选择 / pick a colleague…</option>
                    {(staffRows ?? []).filter((s) => s.active).map((s) => (
                      <option key={s.id} value={s.id}>{s.name}{s.dept ? ` · ${s.dept}` : ''}</option>
                    ))}
                    {/* keep the current holder selectable even before the list loads */}
                    {holderId && !(staffRows ?? []).some((s) => s.id === holderId) && (
                      <option value={holderId}>{holderName ?? holderId}</option>
                    )}
                  </select>
                </div>
              )}

              {canSign && (
                <button className="btn-primary" disabled={busy} style={{ marginTop: 8, minHeight: 44, width: '100%' }}
                  onClick={() => setOpenSeat(seat)}>
                  签字 / Sign{!mine && isGm ? '（代签）' : ''}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {openSeat && (
        <SignDrawer
          detail={detail}
          seat={openSeat}
          onBehalf={!(myStaffId && detail.signatures.find((s) => s.seat === openSeat)?.seat_staff_id === myStaffId)}
          onClose={() => setOpenSeat(null)}
          onDone={() => { setOpenSeat(null); onChanged(); }}
        />
      )}

      {/* the wet-ink page, attached after the fact — the paper stays the legal artefact (华控 9/16) */}
      {wi.state !== 'draft' && (
        <div className="row-wrap small" style={{ marginTop: 10, gap: 8, alignItems: 'center' }}>
          <label className="muted">补传纸质签字扫描件 / attach the wet-ink scan</label>
          <input type="file" accept="image/*,application/pdf" disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const fd = new FormData();
              fd.append('scan', f);
              run(() => attachInstructionWetSignature(wi.id, fd), '扫描件已上传 / scan attached');
              e.target.value = '';
            }} />
          {wi.wet_signature_file_id && <span className="pill pill-good small">✓ 已有扫描件 / scan on file</span>}
        </div>
      )}
    </div>
  );
}

/** One signature. `on_behalf_note_md` is REQUIRED when the signer is not the seat holder (gm 代签). */
function SignDrawer({ detail, seat, onBehalf, onClose, onDone }: {
  detail: InstructionDetail;
  seat: InstructionSeatKey;
  onBehalf: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState('');
  const [behalfNote, setBehalfNote] = useState('');
  const [scan, setScan] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (onBehalf && !behalfNote.trim()) { setErr('代签必须填写代签说明 / an on-behalf signature needs a written note'); return; }
    setBusy(true); setErr(null);
    try {
      if (scan) {
        const fd = new FormData();
        fd.append('seat', seat);
        if (note.trim()) fd.append('note_md', note.trim());
        if (onBehalf) fd.append('on_behalf_note_md', behalfNote.trim());
        fd.append('scan', scan);
        await signWorkInstructionWithScan(detail.instruction.id, fd);
      } else {
        await signWorkInstruction(detail.instruction.id, {
          seat,
          ...(note.trim() ? { note_md: note.trim() } : {}),
          ...(onBehalf ? { on_behalf_note_md: behalfNote.trim() } : {}),
        });
      }
      toast('已签字 / signed', { kind: 'success' });
      onDone();
    } catch (e) {
      // review 9/18: a phone double-tap on the THIRD signature fires twice. The first request flips the
      // order to effective+served inside one tx, so the second comes back 409 not_pending_signatures —
      // which means "it worked, you were just late", not "it failed". A red toast there taught signers
      // to press again. Treat it as done and refresh instead.
      if ((e as ApiError)?.body?.error === 'not_pending_signatures') {
        toast('签字已完成，指令已生效 / already signed — the order is effective', { kind: 'success' });
        onDone();
        return;
      }
      setErr(apiErrText(e));
    }
    finally { setBusy(false); }
  }

  return (
    <div className="card drawer" style={{ marginTop: 10, borderColor: 'var(--accent)' }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <strong>签字 / Sign · {INSTRUCTION_SEAT_LABEL[seat]}</strong>
        <span style={{ flex: 1 }} />
        <button className="small" onClick={onClose} style={{ minHeight: 44 }}>× 关闭 / Close</button>
      </div>
      <div className="small muted" style={{ marginBottom: 8 }}>
        {detail.instruction.doc_no ?? detail.instruction.id} · {detail.instruction.title}
      </div>
      {onBehalf && (
        <div className="field">
          <label>代签说明（必填）/ on-behalf note (required)</label>
          <input value={behalfNote} onChange={(e) => setBehalfNote(e.target.value)} style={{ minHeight: 44 }}
            placeholder="例：席位人出差，经电话确认后由总经理代签 / why you are signing for the seat holder" />
        </div>
      )}
      <div className="field">
        <label>备注 / note（可选 optional）</label>
        <input value={note} onChange={(e) => setNote(e.target.value)} style={{ minHeight: 44 }} />
      </div>
      <div className="field">
        <label>盖章扫描件 / stamped scan（可选 optional · PDF 或照片）</label>
        <input type="file" accept="image/*,application/pdf" onChange={(e) => setScan(e.target.files?.[0] ?? null)} />
      </div>
      {err && <div className="small" style={{ color: 'var(--bad)', marginBottom: 6 }}>{err}</div>}
      <button className="btn-primary" disabled={busy} onClick={submit} style={{ minHeight: 44 }}>
        确认签字 / Confirm signature
      </button>
    </div>
  );
}
